import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Header2 from "../components/Header2";
import Footer from "../components/Footer";
import { Howl } from "howler";
import ConnectModal from "../components/ConnectModal";
import AlertModal from "../components/AlertModal";
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import {
  CONTRACTS,
  ABIS,
  CHAINS,
  DECIMALS,
} from "../config/contracts";
import { parseUnits, formatUnits, maxUint256 } from "viem";

const Staking = () => {
  useEffect(() => {
    const sound = new Howl({ src: ["assets/sounds/open.wav"], volume: 0.6 });
    sound.play();
  }, []);

  const [openConnect, setOpenConnect] = useState(false);
  const [stakeAmount, setStakeAmount] = useState(0);
  const [stakeLoading, setStakeLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [harvestLoading, setHarvestLoading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [pendingStakeAfterApproval, setPendingStakeAfterApproval] =
    useState(false);
  const pendingStakeAmountRef = useRef(null);
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    title: "",
    message: "",
    actions: [],
  });
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));

  const closeAlert = useCallback(() => {
    setAlertConfig((prev) => ({ ...prev, open: false, actions: [] }));
  }, []);

  const showAlert = useCallback((message, title = "Notice", actions = []) => {
    setAlertConfig({
      open: true,
      title,
      message,
      actions,
    });
  }, []);

  const { address, isConnected } = useAccount();

  // Read AIFL token balance
  const { data: tokenBalanceData } = useReadContract({
    address: CONTRACTS.TOKEN,
    abi: ABIS.TOKEN,
    functionName: "balanceOf",
    args: [address],
    chainId: CHAINS.SEPOLIA,
    enabled: !!address,
  });

  // Read token allowance for staking contract
  const { data: tokenAllowanceData, refetch: refetchAllowance } =
    useReadContract({
      address: CONTRACTS.TOKEN,
      abi: ABIS.TOKEN,
      functionName: "allowance",
      args: [address, CONTRACTS.STAKING],
      chainId: CHAINS.SEPOLIA,
      enabled: !!address,
    });

  // Read total tokens staked
  const { data: totalStakedData } = useReadContract({
    address: CONTRACTS.STAKING,
    abi: ABIS.STAKING,
    functionName: "tokensStaked",
    chainId: CHAINS.SEPOLIA,
  });

  // Read APR
  const { data: aprData } = useReadContract({
    address: CONTRACTS.STAKING,
    abi: ABIS.STAKING,
    functionName: "apr",
    chainId: CHAINS.SEPOLIA,
  });

  // Read user staking info
  const { data: userStakingData, refetch: refetchUserStaking } =
    useReadContract({
      address: CONTRACTS.STAKING,
      abi: ABIS.STAKING,
      functionName: "poolStakers",
      args: [address],
      chainId: CHAINS.SEPOLIA,
      enabled: !!address,
    });

  // Read user rewards
  const { data: userRewardsData, refetch: refetchRewards } = useReadContract({
    address: CONTRACTS.STAKING,
    abi: ABIS.STAKING,
    functionName: "getRewards",
    args: [address],
    chainId: CHAINS.SEPOLIA,
    enabled: !!address,
  });

  // Presale deposits (always fetched for stakeable calculation)
  const { data: userDepositsData } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: "userDeposits",
    args: [address],
    chainId: CHAINS.SEPOLIA,
    enabled: !!address,
    watch: true,
  });

  // Read user staked by presale
  const { data: userStakedByPresaleData, refetch: refetchUserStakedByPresale } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: "userStakedByPresale",
    args: [address],
    chainId: CHAINS.SEPOLIA,
    enabled: !!address,
    watch: true,
  });

  // Read claim start time
  const { data: claimStartData } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: "claimStart",
    chainId: CHAINS.SEPOLIA,
    watch: true,
  });

  // Check if user has claimed
  const { data: hasClaimedData } = useReadContract({
    address: CONTRACTS.PRESALE,
    abi: ABIS.PRESALE,
    functionName: "hasClaimed",
    args: [address],
    chainId: CHAINS.SEPOLIA,
    enabled: !!address,
    watch: true,
  });

  // Write contracts
  const { writeContract: writeApprove, data: approveHash, error: writeApproveError, isError: isWriteApproveError, reset: resetApprove } = useWriteContract();
  const { writeContract: writeStake, data: stakeHash, error: writeStakeError, isError: isWriteStakeError, reset: resetStake } = useWriteContract();
  const { writeContract: writeStakeFromPresale, data: stakeFromPresaleHash, error: writeStakeFromPresaleError, isError: isWriteStakeFromPresaleError, reset: resetStakeFromPresale } = useWriteContract();
  const { writeContract: writeWithdraw, data: withdrawHash, error: writeWithdrawError, isError: isWriteWithdrawError, reset: resetWithdraw } =
    useWriteContract();
  const { writeContract: writeHarvest, data: harvestHash, error: writeHarvestError, isError: isWriteHarvestError, reset: resetHarvest } = useWriteContract();

  // Transaction receipts
  const { isLoading: isApproving, isSuccess: isApproved, isError: isApprovalError, error: approvalError } =
    useWaitForTransactionReceipt({ hash: approveHash });
  const { isLoading: isStaking, isSuccess: isStaked, isError: isStakeError, error: stakeError } =
    useWaitForTransactionReceipt({ hash: stakeHash });
  const { isLoading: isStakingFromPresale, isSuccess: isStakedFromPresale, isError: isStakeFromPresaleError, error: stakeFromPresaleError } =
    useWaitForTransactionReceipt({ hash: stakeFromPresaleHash });
  const { isLoading: isWithdrawing, isSuccess: isWithdrawn, isError: isWithdrawError, error: withdrawError } =
    useWaitForTransactionReceipt({ hash: withdrawHash });
  const { isLoading: isHarvesting, isSuccess: isHarvested, isError: isHarvestError, error: harvestError } =
    useWaitForTransactionReceipt({ hash: harvestHash });

  // Format data
  const tokenBalance = tokenBalanceData
    ? Number(formatUnits(tokenBalanceData, DECIMALS.TOKEN)).toFixed(2)
    : "0.00";
  const totalStaked = totalStakedData
    ? Number(formatUnits(totalStakedData, DECIMALS.TOKEN)).toFixed(0)
    : "0";
  const apr = aprData ? Number(aprData) : 0; // APR is stored as basis points (e.g., 12000 = 120%)

  // User staking data
  const userStakedAmount = userStakingData
    ? Number(formatUnits(userStakingData[0], DECIMALS.TOKEN)).toFixed(2)
    : "0.00";
  const userRewards = userRewardsData
    ? Number(formatUnits(userRewardsData, DECIMALS.TOKEN)).toFixed(2)
    : "0.00";
  
  // Lock period check
  const lockUntil = userStakingData && userStakingData[5] ? Number(userStakingData[5]) : 0;
  const nowSeconds = currentTime;
  const isLocked = lockUntil > 0 && nowSeconds < lockUntil;
  const timeUntilUnlock = isLocked ? lockUntil - nowSeconds : 0;
  
  // Format time until unlock
  const formatTimeUntilUnlock = (seconds) => {
    if (seconds <= 0) return "0 seconds";
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const parts = [];
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    if (secs > 0 && days === 0 && hours === 0) parts.push(`${secs} second${secs !== 1 ? 's' : ''}`);
    
    return parts.length > 0 ? parts.join(', ') : "0 seconds";
  };
  
  const unlockTimeFormatted = formatTimeUntilUnlock(timeUntilUnlock);
  const userDeposits = userDepositsData
    ? Number(formatUnits(userDepositsData, DECIMALS.TOKEN)).toFixed(2)
    : "0.00";
  const userStakedByPresale = userStakedByPresaleData
    ? Number(formatUnits(userStakedByPresaleData, DECIMALS.TOKEN)).toFixed(2)
    : "0.00";
  // Check if claim has started
  const claimStartSeconds = claimStartData ? Number(claimStartData) : 0;
  const isClaimStarted = claimStartSeconds === 0 ? true : nowSeconds >= claimStartSeconds;
  const isClaimWindowOpen = isClaimStarted;
  const timeUntilClaimStart = !isClaimStarted && claimStartSeconds > 0 ? claimStartSeconds - nowSeconds : 0;
  const claimStartTimeFormatted = formatTimeUntilUnlock(timeUntilClaimStart);
  
  // Calculate stakeable amount:
  // - If claim window is open AND user has claimed: use wallet balance (no subtraction)
  // - Otherwise: use presale purchases (userDeposits) minus already staked
  const userStakedNumeric = userStakingData ? Number(formatUnits(userStakingData[0] || 0n, DECIMALS.TOKEN)) : 0;
  let userStakeableNumeric;
  if (isClaimWindowOpen && hasClaimedData) {
    // After claiming: stakeable is just wallet balance
    userStakeableNumeric = tokenBalanceData ? Number(formatUnits(tokenBalanceData, DECIMALS.TOKEN)) : 0;
  } else {
    // Before claiming: stakeable is presale purchases minus already staked
    const userPurchasedNumeric = userDepositsData ? Number(formatUnits(userDepositsData, DECIMALS.TOKEN)) : 0;
    userStakeableNumeric = Math.max(0, userPurchasedNumeric - userStakedNumeric);
  }
  const stakeableAmount = Math.floor(userStakeableNumeric).toFixed(0);
  
  // Display: use wallet balance when claim is active and user has claimed, otherwise use deposits
  const availableDisplay = (isClaimWindowOpen && hasClaimedData)
    ? tokenBalance
    : userDeposits;
  const availableCaption = (isClaimWindowOpen && hasClaimedData)
    ? "Wallet Balance"
    : "Your Purchased";

  const isUserRejected = useCallback((error) => {
    const message = (error?.shortMessage || error?.message || "").toLowerCase();
    return (
      error?.name === "UserRejectedRequestError" ||
      error?.code === 4001 ||
      message.includes("user rejected") ||
      message.includes("user canceled") ||
      message.includes("user cancelled")
    );
  }, []);

  const notifyTxError = useCallback(
    (error, fallbackMessage, action = "Action") => {
      if (isUserRejected(error)) {
        showAlert(`${action} canceled.`, "Action Canceled");
      } else {
        showAlert(fallbackMessage, "Error");
      }
    },
    [showAlert]
  );

  // Handle writeContract errors (immediate user cancellations in MetaMask)
  useEffect(() => {
    if (isWriteApproveError && writeApproveError) {
      setApprovalLoading(false);
      setPendingStakeAfterApproval(false);
      pendingStakeAmountRef.current = null;
      if (isUserRejected(writeApproveError)) {
        showAlert('Approval canceled.', 'Action Canceled');
      }
      resetApprove();
    }
  }, [isWriteApproveError, writeApproveError, showAlert, isUserRejected, resetApprove]);

  useEffect(() => {
    if (isWriteStakeError && writeStakeError) {
      setStakeLoading(false);
      setPendingStakeAfterApproval(false);
      pendingStakeAmountRef.current = null;
      if (isUserRejected(writeStakeError)) {
        showAlert('Stake canceled.', 'Action Canceled');
      }
      resetStake();
    }
  }, [isWriteStakeError, writeStakeError, showAlert, isUserRejected, resetStake]);

  useEffect(() => {
    if (isWriteStakeFromPresaleError && writeStakeFromPresaleError) {
      setStakeLoading(false);
      setPendingStakeAfterApproval(false);
      pendingStakeAmountRef.current = null;
      if (isUserRejected(writeStakeFromPresaleError)) {
        showAlert('Stake canceled.', 'Action Canceled');
      }
      resetStakeFromPresale();
    }
  }, [isWriteStakeFromPresaleError, writeStakeFromPresaleError, showAlert, isUserRejected, resetStakeFromPresale]);

  useEffect(() => {
    if (isWriteWithdrawError && writeWithdrawError) {
      setWithdrawLoading(false);
      if (isUserRejected(writeWithdrawError)) {
        showAlert('Withdraw canceled.', 'Action Canceled');
      }
      resetWithdraw();
    }
  }, [isWriteWithdrawError, writeWithdrawError, showAlert, isUserRejected, resetWithdraw]);

  useEffect(() => {
    if (isWriteHarvestError && writeHarvestError) {
      setHarvestLoading(false);
      if (isUserRejected(writeHarvestError)) {
        showAlert('Claim Rewards canceled.', 'Action Canceled');
      }
      resetHarvest();
    }
  }, [isWriteHarvestError, writeHarvestError, showAlert, isUserRejected, resetHarvest]);

  // Handle transaction receipt errors (after transaction is sent)
  useEffect(() => {
    if (isApprovalError && approvalError) {
      setApprovalLoading(false);
      setPendingStakeAfterApproval(false);
      pendingStakeAmountRef.current = null;
      if (isUserRejected(approvalError)) {
        showAlert('Approval canceled.', 'Action Canceled');
      }
    }
  }, [isApprovalError, approvalError, showAlert, isUserRejected]);

  useEffect(() => {
    if (isStakeError && stakeError) {
      setStakeLoading(false);
      setPendingStakeAfterApproval(false);
      pendingStakeAmountRef.current = null;
      if (isUserRejected(stakeError)) {
        showAlert('Stake canceled.', 'Action Canceled');
      }
    }
  }, [isStakeError, stakeError, showAlert, isUserRejected]);

  useEffect(() => {
    if (isStakeFromPresaleError && stakeFromPresaleError) {
      setStakeLoading(false);
      setPendingStakeAfterApproval(false);
      pendingStakeAmountRef.current = null;
      if (isUserRejected(stakeFromPresaleError)) {
        showAlert('Stake canceled.', 'Action Canceled');
      }
    }
  }, [isStakeFromPresaleError, stakeFromPresaleError, showAlert, isUserRejected]);

  useEffect(() => {
    if (isWithdrawError && withdrawError) {
      setWithdrawLoading(false);
      if (isUserRejected(withdrawError)) {
        showAlert('Withdraw canceled.', 'Action Canceled');
      }
    }
  }, [isWithdrawError, withdrawError, showAlert, isUserRejected]);

  useEffect(() => {
    if (isHarvestError && harvestError) {
      setHarvestLoading(false);
      if (isUserRejected(harvestError)) {
        showAlert('Claim Rewards canceled.', 'Action Canceled');
      }
    }
  }, [isHarvestError, harvestError, showAlert, isUserRejected]);

  // Auto-fill input with stakeable amount only if input is empty or zero
  useEffect(() => {
    if (stakeableAmount && Number(stakeableAmount) > 0 && (!stakeAmount || Number(stakeAmount) === 0)) {
      setStakeAmount(stakeableAmount);
    }
  }, [stakeableAmount]);

  // Update current time every second when locked or claim hasn't started
  useEffect(() => {
    if (isLocked || !isClaimStarted) {
      const interval = setInterval(() => {
        setCurrentTime(Math.floor(Date.now() / 1000));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }
  }, [isLocked, isClaimStarted]);

  useEffect(() => {
    if (!address) return;
    const interval = setInterval(() => {
      refetchAllowance();
      refetchUserStaking();
      refetchRewards();
      refetchUserStakedByPresale();
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 15000);
    return () => clearInterval(interval);
  }, [address, refetchAllowance, refetchUserStaking, refetchRewards, refetchUserStakedByPresale]);

  // Handle transaction completions
  useEffect(() => {
    if (isApproved) {
      refetchAllowance();
      setApprovalLoading(false);

      // If we were waiting to stake after approval, proceed with staking
      if (pendingStakeAfterApproval && pendingStakeAmountRef.current) {
        const amount = pendingStakeAmountRef.current;
        const nowSeconds = Math.floor(Date.now() / 1000);
        const claimStartSeconds = claimStartData ? Number(claimStartData) : 0;
        const isClaimStarted = claimStartSeconds === 0 ? true : nowSeconds >= claimStartSeconds;
        
        if (!isClaimStarted) {
          // Use stakeFromPresale if claim hasn't started
          writeStakeFromPresale({
            address: CONTRACTS.PRESALE,
            abi: ABIS.PRESALE,
            functionName: "stakeFromPresale",
            args: [amount],
            chainId: CHAINS.SEPOLIA,
          });
        } else {
          // Use normal staking deposit if claim has started
          writeStake({
            address: CONTRACTS.STAKING,
            abi: ABIS.STAKING,
            functionName: "deposit",
            args: [amount],
            chainId: CHAINS.SEPOLIA,
          });
        }
        setPendingStakeAfterApproval(false);
        pendingStakeAmountRef.current = null;
      } else if (!pendingStakeAfterApproval) {
        // Only show approval message if it wasn't part of a stake flow
        showAlert("Token approved! You can now stake.", "Success");
      }
    }
  }, [
    isApproved,
    refetchAllowance,
    pendingStakeAfterApproval,
    writeStake,
    writeStakeFromPresale,
    claimStartData,
    showAlert,
  ]);

  useEffect(() => {
    if (isStaked) {
      refetchUserStaking();
      refetchUserStakedByPresale();
      setStakeLoading(false);
      setStakeAmount(0);
      setPendingStakeAfterApproval(false);
      pendingStakeAmountRef.current = null;
      showAlert("Staking successful!", "Success");
    }
  }, [isStaked, refetchUserStaking, refetchUserStakedByPresale, showAlert]);

  useEffect(() => {
    if (isStakedFromPresale) {
      refetchUserStaking();
      refetchUserStakedByPresale();
      setStakeLoading(false);
      setStakeAmount(0);
      setPendingStakeAfterApproval(false);
      pendingStakeAmountRef.current = null;
      showAlert("Staking successful!", "Success");
    }
  }, [isStakedFromPresale, refetchUserStaking, refetchUserStakedByPresale, showAlert]);

  useEffect(() => {
    if (isWithdrawn) {
      refetchUserStaking();
      refetchRewards();
      setWithdrawLoading(false);
      showAlert("Withdrawal successful!", "Success");
    }
  }, [isWithdrawn, refetchUserStaking, refetchRewards, showAlert]);

  useEffect(() => {
    if (isHarvested) {
      refetchRewards();
      setHarvestLoading(false);
      showAlert("Rewards claimed successfully!", "Success");
    }
  }, [isHarvested, refetchRewards, showAlert]);

  // Handler functions
  const handleApprove = async () => {
    if (!isConnected) {
      setOpenConnect(true);
      return;
    }
    try {
      setApprovalLoading(true);
      writeApprove({
        address: CONTRACTS.TOKEN,
        abi: ABIS.TOKEN,
        functionName: "approve",
        args: [CONTRACTS.STAKING, maxUint256],
        chainId: CHAINS.SEPOLIA,
      });
    } catch (error) {
      console.error("Approve error:", error);
      setApprovalLoading(false);
      setPendingStakeAfterApproval(false);
      notifyTxError(error, "Failed to approve tokens", "Approval");
    }
  };

  const handleStake = async () => {
    if (!isConnected) {
      setOpenConnect(true);
      return;
    }

    if (!stakeAmount || stakeAmount <= 0) {
      showAlert("Please enter a valid amount", "Validation");
      return;
    }

    // Validate that input doesn't exceed stakeable amount
    const stakeableNum = Number(stakeableAmount);
    const inputNum = Number(stakeAmount);
    
    if (stakeableNum <= 0) {
      showAlert(
        "No stakeable tokens available. You need to purchase tokens first.",
        "Validation"
      );
      return;
    }

    if (inputNum > stakeableNum) {
      showAlert(
        `Amount exceeds stakeable balance. Maximum stakeable: ${stakeableAmount} AIFL`,
        "Validation"
      );
      return;
    }

    try {
      const amount = parseUnits(stakeAmount.toString(), DECIMALS.TOKEN);
      const nowSeconds = Math.floor(Date.now() / 1000);
      const claimStartSeconds = claimStartData ? Number(claimStartData) : 0;
      const isClaimStarted = claimStartSeconds === 0 ? true : nowSeconds >= claimStartSeconds;

      if (!isClaimStarted) {
        // Before claim starts: use stakeFromPresale (no approval needed)
        setStakeLoading(true);
        writeStakeFromPresale({
          address: CONTRACTS.PRESALE,
          abi: ABIS.PRESALE,
          functionName: "stakeFromPresale",
          args: [amount],
          chainId: CHAINS.SEPOLIA,
        });
      } else {
        // After claim starts: use normal staking (approval needed)
        // Check if approval is needed
        if (!tokenAllowanceData || tokenAllowanceData < amount) {
          // Need to approve first, then stake
          setPendingStakeAfterApproval(true);
          pendingStakeAmountRef.current = amount;
          setStakeLoading(true);
          setApprovalLoading(true);
          writeApprove({
            address: CONTRACTS.TOKEN,
            abi: ABIS.TOKEN,
            functionName: "approve",
            args: [CONTRACTS.STAKING, maxUint256],
            chainId: CHAINS.SEPOLIA,
          });
          return;
        }

        // Approval is sufficient, proceed with staking
        setStakeLoading(true);
        writeStake({
          address: CONTRACTS.STAKING,
          abi: ABIS.STAKING,
          functionName: "deposit",
          args: [amount],
          chainId: CHAINS.SEPOLIA,
        });
      }
    } catch (error) {
      console.error("Stake error:", error);
      const wasApproving = approvalLoading || pendingStakeAfterApproval;
      setStakeLoading(false);
      setApprovalLoading(false);
      setPendingStakeAfterApproval(false);
      notifyTxError(error, "Failed to stake tokens", wasApproving ? "Approval" : "Stake");
    }
  };

  const handleWithdraw = async () => {
    if (!isConnected) {
      setOpenConnect(true);
      return;
    }

    if (!userStakingData || userStakingData[0] === 0n) {
      showAlert("No staked tokens to withdraw", "Validation");
      return;
    }

    // Check if tokens are locked
    if (isLocked) {
      showAlert(
        `Tokens are still locked. Unlock time: ${unlockTimeFormatted}`,
        "Withdrawal Locked"
      );
      return;
    }

    try {
      setWithdrawLoading(true);
      writeWithdraw({
        address: CONTRACTS.STAKING,
        abi: ABIS.STAKING,
        functionName: "withdraw",
        chainId: CHAINS.SEPOLIA,
      });
    } catch (error) {
      console.error("Withdraw error:", error);
      setWithdrawLoading(false);
      notifyTxError(error, "Failed to withdraw tokens", "Withdraw");
    }
  };

  const handleHarvest = async () => {
    if (!isConnected) {
      setOpenConnect(true);
      return;
    }

    if (!userRewardsData || userRewardsData === 0n) {
      showAlert("No rewards to claim", "Validation");
      return;
    }

    // Check if claim has started
    if (!isClaimStarted) {
      showAlert(
        `Rewards cannot be claimed yet. Claim starts in: ${claimStartTimeFormatted}`,
        "Claim Not Started"
      );
      return;
    }

    try {
      setHarvestLoading(true);
      writeHarvest({
        address: CONTRACTS.STAKING,
        abi: ABIS.STAKING,
        functionName: "harvestRewards",
        chainId: CHAINS.SEPOLIA,
      });
    } catch (error) {
      console.error("Harvest error:", error);
      setHarvestLoading(false);
      notifyTxError(error, "Failed to claim rewards", "Claim Rewards");
    }
  };

  function addCommas(num) {
    const [whole, decimal] = num.toString().split('.');
    const wholeWithCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimal ? `${wholeWithCommas}.${decimal}` : wholeWithCommas;
  }

  return (
    <>
      <AlertModal
        isOpen={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        actions={alertConfig.actions}
        onClose={closeAlert}
      />
      <Header2
        openModal={() => {
          setOpenConnect(true);
        }}
      />
      <main className="main">
        <div className="staking" style={{ marginBottom: "100px" }}>
          {openConnect ? (
            <ConnectModal closeModal={() => setOpenConnect(false)} />
          ) : (
            ""
          )}
          <div className="wrapper">
            <div className="staking-top">
              <div
                className="staking-left2 dash-order1"
                style={{ opacity: "1" }}
              >
                <div className="wrapper">
                  <div className="staking-bottom-content">
                    <h3>Understanding the AIFL Service and Staking Benefits</h3>
                    <div className="intro">
                      <p>
                        Congrats! You're early to the party! Buy and Stake now
                        during Presale to max out your rewards before the price
                        skyrockets!
                      </p>
                    </div>
                    <div className="p">
                      <div>
                        Here are some key features of our Staking service:
                        <ul>
                          <li>
                            Access to AIFL for querying specific data from our
                            database dataset.
                          </li>
                          <li>
                            Sentiment Analysis based on community member
                            engagement and trustability scores.
                          </li>
                          <li>
                            Detailed project insights, including direct pros and
                            cons assessed by various AIs, with summarized
                            information accessible on the web application.
                          </li>
                          <li>
                            Capability to request specialized analysis of any
                            project by providing its website link, utilizing our
                            LLM AI system for real-time evaluation and rating.
                          </li>
                          <li>
                            Customizable alerts based on pre-set criteria in our
                            application, enabling users to receive timely
                            information about new launches that meet their
                            conditions via socials, emails, etc.
                          </li>
                          <li>Exclusive {apr}% annually staking rewards</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="staking-right"
                style={{
                  translate: "none",
                  rotate: "none",
                  scale: "none",
                  transform: "translate(0px)",
                  opacity: "1",
                }}
              >
                <div className="card total">
                  <div className="total-heading">
                    <div className="total-heading-left">
                      <div className="sub">Staking</div>
                      <h5>Total Staked:</h5>
                    </div>
                    <Link to="/dashboard">
                      <div className="btn" data-colors="primary">
                        <span className="btn-txt">Buy AIFL</span>
                        <div className="corner" data-colors="primary">
                          <span className="corner-left"></span>
                          <span className="corner-right"></span>
                          <span className="corner-top-bottom"></span>
                        </div>
                      </div>
                    </Link>
                  </div>
                  <div className="total-input">
                    <img
                      src="/assets/logo-only.svg"
                      alt="AIFL"
                      width="346"
                      height="255"
                      loading="lazy"
                    />
                    <div className="total-input-content">
                      <strong>{addCommas(totalStaked)}</strong>
                    </div>
                    <div className="corner reverse" data-colors="tertiary">
                      <span className="corner-left"></span>
                      <span className="corner-right"></span>
                      <span className="corner-top-bottom"></span>
                    </div>
                  </div>
                  <div>
                    <div className="total-heading-left">
                      <h5 style={{ marginBottom: "20px" }}>
                        Staking Event Details:
                      </h5>
                    </div>
                    <div className="total-input">
                      APR
                      <div className="total-input-content">
                        <strong>{apr} %</strong>
                      </div>
                      <div className="corner reverse" data-colors="tertiary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </div>
                    <div style={{ marginTop: "1.2em" }} className="total-input">
                      Your Rewards
                      <div className="total-input-content">
                        <strong>{userRewards}</strong>
                      </div>
                      <div className="corner reverse" data-colors="tertiary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </div>
                    <div style={{ marginTop: "1.2em" }} className="total-input">
                      Your Stakeable
                      <div className="total-input-content">
                        <strong>{stakeableAmount} AIFL</strong>
                      </div>
                      <div className="corner reverse" data-colors="tertiary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </div>
                  </div>
                  <div className="corner reverse" data-colors="tertiary">
                    <span className="corner-left"></span>
                    <span className="corner-right"></span>
                    <span className="corner-top-bottom"></span>
                  </div>
                </div>
                <div className="staking-separator" data-status="warning">
                  <div className="cube">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      aria-hidden="true"
                      role="img"
                      className="iconify iconify--carbon"
                      width="1em"
                      height="1em"
                      viewBox="0 0 32 32"
                    >
                      <path
                        fill="currentColor"
                        d="M24 14h-2V8a6 6 0 0 0-12 0v6H8a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V16a2 2 0 0 0-2-2M12 8a4 4 0 0 1 8 0v6h-8Zm12 20H8V16h16Z"
                      ></path>
                    </svg>
                  </div>
                </div>
                <div className="card unlock">
                  <div className="unlock-title">
                    <h5>Your Staking details:</h5>
                  </div>
                  <div className="" style={{ marginBottom: "0" }}>
                    <div className="total-input">
                      Staked Balance
                      <div className="total-input-content">
                        <strong>{userStakedAmount} AIFL</strong>
                      </div>
                      <div className="corner" data-colors="tertiary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </div>
                    <div style={{ marginTop: "1.2em" }} className="total-input">
                      {availableCaption}
                      <div className="total-input-content">
                        <strong>{availableDisplay} AIFL</strong>
                      </div>
                      <div className="corner" data-colors="tertiary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </div>
                    <div
                      style={{ marginTop: "1.2em", marginBottom: "1.2em" }}
                      className="total-input"
                    >
                      Amount to Stake
                      <input
                        className="pay-input"
                        value={stakeAmount}
                        onChange={(e) => setStakeAmount(e.target.value)}
                        type="number"
                        placeholder="0.00"
                        style={{ fontSize: "clamp(1.5em, 2vw, 1.5em)" }}
                      />
                      <div className="corner" data-colors="tertiary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      marginBottom: "12px",
                    }}
                  >
                    <button
                      className="btn"
                      data-colors="primary"
                      style={{ flex: "1" }}
                      onClick={!isConnected ? () => setOpenConnect(true) : handleStake}
                      disabled={
                        stakeLoading ||
                        isStaking ||
                        isStakingFromPresale ||
                        approvalLoading ||
                        isApproving
                      }
                    >
                      <span className="btn-txt">
                        {approvalLoading || isApproving
                          ? "Approving..."
                          : stakeLoading || isStaking || isStakingFromPresale
                          ? "Staking..."
                          : "Stake"}
                      </span>
                      <div className="corner" data-colors="primary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </button>
                    <button
                      className="btn"
                      data-colors="primary"
                      data-status="warning"
                      style={{ flex: "1" }}
                      onClick={!isConnected ? () => setOpenConnect(true) : handleHarvest}
                      disabled={harvestLoading || isHarvesting}
                    >
                      <span className="btn-txt">
                        {harvestLoading || isHarvesting
                          ? "Claiming..."
                          : "Claim Rewards"}
                      </span>
                      <div className="corner" data-colors="primary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </button>
                  </div>
                  <div>
                    <button
                      className="btn"
                      data-colors="primary"
                      style={{ width: "100%" }}
                      onClick={
                        isConnected
                          ? handleWithdraw
                          : () => setOpenConnect(true)
                      }
                      disabled={withdrawLoading || isWithdrawing}
                    >
                      <span className="btn-txt">
                        {!isConnected
                          ? "Connect Wallet"
                          : withdrawLoading || isWithdrawing
                          ? "Withdrawing..."
                          : "Withdraw Staked Tokens"}
                      </span>
                      <div className="corner" data-colors="primary">
                        <span className="corner-left"></span>
                        <span className="corner-right"></span>
                        <span className="corner-top-bottom"></span>
                      </div>
                    </button>
                  </div>
                  <div className="corner" data-colors="tertiary">
                    <span className="corner-left"></span>
                    <span className="corner-right"></span>
                    <span className="corner-top-bottom"></span>
                  </div>
                  {!hasClaimedData && (
                    <div className="unlock-title">
                      <span>Just want to claim purchased AIFL?</span>
                      <Link to="/dashboard">
                        <div
                          className="btn"
                          data-colors="primary"
                          data-status="warning"
                        >
                          <span className="btn-txt">Go to Claim</span>
                          <div className="corner" data-colors="primary">
                            <span className="corner-left"></span>
                            <span className="corner-right"></span>
                            <span className="corner-top-bottom"></span>
                          </div>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>
                <div className="more">
                  <div className="more-arrow">
                    <div className="more-arrow-shape"></div>
                  </div>
                  <a href="#bottom" className="sub">
                    More infos
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Staking;
